import Loader from '@/components/Loader';
import BaseLayout from '@/layout/BaseLayout';
import {
  batchActionTypeLabels,
  batchItemStatusLabel,
  batchItemStatusLabels,
  batchRefetchInterval,
  batchStatusClasses,
  batchStatusLabels,
} from '@/screens/BatchActionList/batchActionLabels';
import { trpc } from '@/trpcClient';
import { GenericTable } from '@shelflix/ui-web/components/GenericTable';
import { Badge } from '@shelflix/ui-web/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

const PAGE_SIZE = 20;
const ALL_STATUSES = 'all';

type ItemStatus = 'pending' | 'processing' | 'succeeded' | 'failed';

const itemStatusClasses: Record<string, string> = {
  pending: batchStatusClasses.pending,
  processing: batchStatusClasses.processing,
  succeeded: batchStatusClasses.completed,
  failed: batchStatusClasses.failed,
};

function useBatchDetail(uuid: string, itemStatus: ItemStatus | undefined, page: number) {
  return useQuery(
    trpc.batchAction.detail.queryOptions(
      { uuid, itemStatus, page, pageSize: PAGE_SIZE },
      {
        placeholderData: keepPreviousData,
        refetchInterval: (query) => batchRefetchInterval(query.state.data ? [query.state.data.status] : undefined),
      }
    )
  );
}

type BatchDetail = NonNullable<ReturnType<typeof useBatchDetail>['data']>;
type ItemRow = BatchDetail['items']['rows'][number] & { uuid: string };

type Column = {
  header: string;
  key: keyof ItemRow;
  render: (value: ItemRow[keyof ItemRow], row: ItemRow) => ReactNode;
};

const formatDateTime = (value: string | null) => (value ? dayjs(value).format('DD/MM/YYYY - HH:mm:ss') : '-');

const columns: Column[] = [
  {
    header: 'Material',
    key: 'material',
    render: (_, row) => (
      <Link to={`/pos/${row.material.uuid}`} className="text-[var(--primary)] underline">
        {row.material.name}
      </Link>
    ),
  },
  {
    header: 'Status',
    key: 'status',
    render: (_, row) => (
      <Badge
        className={`h-6 rounded-sm px-2 py-[5px] text-xs font-medium ${row.notProcessed || row.interrupted ? batchStatusClasses.interrupted : (itemStatusClasses[row.status] ?? '')}`}
      >
        {batchItemStatusLabel(row)}
      </Badge>
    ),
  },
  {
    header: 'Erro',
    key: 'errorMessage',
    render: (_, row) => <span className="break-words whitespace-pre-wrap">{row.errorMessage || '-'}</span>,
  },
  { header: 'Início', key: 'startedAt', render: (_, row) => formatDateTime(row.startedAt) },
  { header: 'Fim', key: 'finishedAt', render: (_, row) => formatDateTime(row.finishedAt) },
];

function SummaryField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-[var(--gray-primary)]">{label}</span>
      <span className="text-sm">{children}</span>
    </div>
  );
}

function BatchSummary({ batch }: { batch: BatchDetail }) {
  const filters = [
    { label: 'Marca', value: batch.filters.brand?.name },
    { label: 'Tipo de material', value: batch.filters.materialType?.name },
    { label: 'UF', value: batch.filters.state?.name },
    { label: 'Cidade', value: batch.filters.city?.name },
    { label: 'Tag', value: batch.filters.tag?.name },
  ].filter((filter) => filter.value);

  return (
    <div className="flex flex-col gap-4 rounded-[10px] bg-white p-4 dark:bg-transparent">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <SummaryField label="Tipo de ação">{batchActionTypeLabels[batch.actionType] ?? batch.actionType}</SummaryField>
        <SummaryField label="Campanha">{batch.campaign?.name ?? '-'}</SummaryField>
        <SummaryField label="Usuário">{batch.user?.name ?? '-'}</SummaryField>
        <SummaryField label="Início">{formatDateTime(batch.startedAt)}</SummaryField>
        <SummaryField label="Fim">{formatDateTime(batch.finishedAt)}</SummaryField>
        <SummaryField label="Status">
          <Badge
            className={`h-6 rounded-sm px-2 py-[5px] text-xs font-medium ${batchStatusClasses[batch.status] ?? ''}`}
          >
            {batchStatusLabels[batch.status] ?? batch.status}
          </Badge>
        </SummaryField>
      </div>
      {filters.length > 0 && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {filters.map((filter) => (
            <SummaryField key={filter.label} label={filter.label}>
              {filter.value}
            </SummaryField>
          ))}
        </div>
      )}
    </div>
  );
}

export function BatchActionDetail() {
  const { uuid = '' } = useParams<{ uuid: string }>();
  const [itemStatus, setItemStatus] = useState<ItemStatus | undefined>(undefined);
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useBatchDetail(uuid, itemStatus, page);

  const rows: ItemRow[] = (data?.items.rows ?? []).map((row) => ({ ...row, uuid: row.material.uuid }));

  const changeItemStatus = (value: string) => {
    setItemStatus(value === ALL_STATUSES ? undefined : (value as ItemStatus));
    setPage(1);
  };

  return (
    <BaseLayout>
      <section className="flex w-full flex-col gap-4 px-4 md:px-0">
        {isLoading && <Loader message="Carregando ação em lote..." />}
        {isError && <p className="p-4 text-red-500">Erro ao carregar a ação em lote.</p>}
        {data && (
          <>
            <BatchSummary batch={data} />
            {!data.detailsAvailable ? (
              <p className="p-4">Detalhes indisponíveis</p>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[var(--gray-primary)]">Status do item</span>
                  <Select value={itemStatus ?? ALL_STATUSES} onValueChange={changeItemStatus}>
                    <SelectTrigger className="w-[200px]" aria-label="Filtrar por status do item">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_STATUSES}>Todos</SelectItem>
                      {Object.entries(batchItemStatusLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {rows.length === 0 ? (
                  <p className="p-4">Nenhum item com esse status.</p>
                ) : (
                  <GenericTable
                    columns={columns}
                    data={rows}
                    page={page}
                    itemsPerPages={PAGE_SIZE}
                    totalItems={data.items.total}
                    onPageChange={setPage}
                  />
                )}
              </>
            )}
          </>
        )}
      </section>
    </BaseLayout>
  );
}
